import { useMemo } from 'react';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useListWorkspaceObjectTypes, type WorkspaceObjectType } from '@/features/workspaces';
import { isEmptyString } from '@/utils/string';

/**
 * Workspace forms (custom + Facebook lead-ads) for resolving `form_integration` log sources to a
 * named form link. Lazy — only fetched when a form-integration source actually renders. Cached;
 * scoped to the workspace.
 */
export function useActivityLogForms(environmentId: string, enabled: boolean) {
  const on = enabled && !isEmptyString(environmentId);
  const custom = useHydraUIAPIQuery({
    queryKey: ['activityLogCustomForms', environmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.customForms.list({ params: { path: { environment_id: environmentId } }, signal }),
    queryConfig: { enabled: on, staleTime: 60_000 },
  });
  const facebook = useHydraUIAPIQuery({
    queryKey: ['activityLogFacebookForms', environmentId],
    queryFn: ({ signal }) =>
      hydraUiApi.facebookLeadAds.getFacebookLeadAdsForms({
        params: { path: { environment_id: environmentId } },
        signal,
      }),
    queryConfig: { enabled: on, staleTime: 60_000 },
  });
  return {
    customForms: custom.data?.custom_forms ?? [],
    facebookForms: facebook.data?.facebook_lead_ads_forms ?? [],
    isPending: on && (custom.isLoading || facebook.isLoading),
  };
}

/** The campaign / broadcast an in-app form belongs to, for the form-submissions deep link. */
export type InAppFormParent = { name: string; kind: 'campaign' | 'newsletter'; id: string };

/**
 * Resolves an in-app form's parent campaign/broadcast for the `form_integration` (in-app) source
 * via a dependent-fetch chain: template → its action → that action's campaign, OR template → its
 * newsletter. Each step is a React Query enabled on the prior id (cached + deduped across rows).
 * Lazy: nothing fetches without a `templateId`.
 */
export function useInAppFormParent(environmentId: string, templateId: string | number | null | undefined) {
  const tid = templateId == null ? '' : String(templateId);
  const enabled = !isEmptyString(tid) && !isEmptyString(environmentId);

  const template = useHydraUIAPIQuery({
    queryKey: ['activityLogTemplate', environmentId, tid],
    queryFn: ({ signal }) =>
      hydraUiApi.templates.get({ params: { path: { environment_id: environmentId, template_id: tid } }, signal }),
    queryConfig: { enabled, staleTime: 60_000 },
  });
  const aid = template.data?.template?.action_id == null ? '' : String(template.data.template.action_id);
  const nid = template.data?.template?.newsletter_id == null ? '' : String(template.data.template.newsletter_id);

  const action = useHydraUIAPIQuery({
    queryKey: ['activityLogAction', environmentId, aid],
    queryFn: ({ signal }) =>
      hydraUiApi.actions.get({ params: { path: { environment_id: environmentId, action_id: aid } }, signal }),
    queryConfig: { enabled: !isEmptyString(aid), staleTime: 60_000 },
  });
  const cid = action.data?.action?.campaign_id == null ? '' : String(action.data.action.campaign_id);

  const campaign = useHydraUIAPIQuery({
    queryKey: ['activityLogCampaign', environmentId, cid],
    queryFn: ({ signal }) =>
      hydraUiApi.campaigns.get({ params: { path: { environment_id: environmentId, campaign_id: cid } }, signal }),
    queryConfig: { enabled: !isEmptyString(cid), staleTime: 60_000 },
  });
  const newsletter = useHydraUIAPIQuery({
    queryKey: ['activityLogNewsletter', environmentId, nid],
    queryFn: ({ signal }) =>
      hydraUiApi.newsletters.get({ params: { path: { environment_id: environmentId, id: nid } }, signal }),
    queryConfig: { enabled: !isEmptyString(nid), staleTime: 60_000 },
  });

  let parent: InAppFormParent | null = null;
  if (!isEmptyString(cid) && campaign.data?.campaign != null) {
    parent = { name: campaign.data.campaign.name, kind: 'campaign', id: cid };
  } else if (!isEmptyString(nid) && newsletter.data?.newsletter != null) {
    parent = { name: newsletter.data.newsletter.name, kind: 'newsletter', id: nid };
  }
  const isLoading = enabled && (template.isLoading || action.isLoading || campaign.isLoading || newsletter.isLoading);
  return { parent, isLoading, hasTemplate: enabled };
}

/**
 * Lazily fetch a SQL import by id for the `sql_import` log source, via the typed client
 * (`hydraUiApi.sqlImports.get`). Cached by id; only fetched when a SQL-import source renders.
 */
export function useActivityLogSqlImport(environmentId: string, importId: string | number | null | undefined) {
  const iid = importId == null ? '' : String(importId);
  const enabled = !isEmptyString(iid) && !isEmptyString(environmentId);
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogSqlImport', environmentId, iid],
    queryFn: ({ signal }) =>
      hydraUiApi.sqlImports.get({ params: { path: { environment_id: environmentId, id: iid } }, signal }),
    queryConfig: { enabled, staleTime: 60_000 },
  });
  return {
    sqlImport: query.data?.sql_import ?? null,
    /** The `sql-sync/:type/:id` route's type segment — from the sideloaded database record. */
    databaseType: query.data?.sql_import_database?.type ?? '',
    isPending: enabled && query.isLoading,
  };
}

/**
 * Lazily fetch a single delivery by id — a per-row load, kept lazy on purpose. React Query caches
 * by id, so repeated/adjacent rows that reference the same delivery resolve without a new request.
 * Disabled when there's no id.
 */
export function useActivityLogDelivery(environmentId: string, deliveryId: string | null | undefined) {
  const enabled = !isEmptyString(deliveryId ?? '');
  const query = useHydraUIAPIQuery({
    queryKey: ['activityLogDelivery', environmentId, deliveryId ?? ''],
    queryFn: ({ signal }) =>
      hydraUiApi.deliveries.get({ params: { path: { environment_id: environmentId, id: deliveryId ?? '' } }, signal }),
    queryConfig: { enabled },
  });
  return {
    subject: query.data?.delivery?.subject,
    isPending: enabled && query.isLoading,
    /** Delivery id was set but the record is gone (deleted) — the caller falls through to the raw name. */
    notFound: enabled && !query.isLoading && query.data?.delivery == null,
  };
}

/**
 * The workspace's object types by id, which an object log's cell needs to
 * name the type it belongs to. A non-numeric id disables the read: `Number('')` is `0`, which a
 * `!= null` guard would take for a real workspace and cache under it.
 */
export function useObjectTypeById(workspaceId: string): Map<number, WorkspaceObjectType> {
  const numericId = Number(workspaceId);
  const query = useListWorkspaceObjectTypes(Number.isInteger(numericId) && numericId > 0 ? numericId : undefined);
  return useMemo(() => new Map((query.data ?? []).map((type) => [type.id, type])), [query.data]);
}
